/**
 * Utility functions to print Ficha Socio and CtaCte views
 */

// Helper to format currency exactly like the screenshots: $15,250.00
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
};

/**
 * Prints the Ficha Socio document matching the layout from the user's screenshot
 * @param {Object} afiliado The main affiliate details (including Domicilio, Plan, Zona, and Familiares list)
 */
export const printFichaSocio = (afiliado) => {
  if (!afiliado) return;

  const printWindow = window.open('', '_blank', 'width=900,height=800');
  if (!printWindow) {
    alert('Por favor, permite las ventanas emergentes (popups) para poder imprimir.');
    return;
  }

  // Filter only active family members
  const activeFamiliares = (afiliado.familiares || []).filter(f => f.estado && f.estado.trim() === 'A');

  // Generate family members rows
  const familiaresRows = activeFamiliares.map(f => `
    <tr>
      <td style="text-align: center; border-bottom: 1px solid #e0e0e0; padding: 6px 0;">${f.dni || ''}</td>
      <td style="text-align: left; padding-left: 20px; border-bottom: 1px solid #e0e0e0;">${f.nombre || ''}</td>
      <td style="text-align: center; border-bottom: 1px solid #e0e0e0;">${f.fecnac || ''}</td>
      <td style="text-align: left; padding-left: 20px; border-bottom: 1px solid #e0e0e0;">${f.relacion || f.categoria || ''}</td>
      <td style="text-align: center; border-bottom: 1px solid #e0e0e0;">${f.fecvigencia || ''}</td>
    </tr>
  `).join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Ficha Socio - ${afiliado.nombre}</title>
      <style>
        @page {
          size: A4 portrait;
          margin: 15mm 15mm 15mm 15mm;
        }
        body {
          font-family: 'Arial', sans-serif;
          margin: 0;
          padding: 0;
          color: #000000;
          font-size: 13px;
          line-height: 1.4;
          background-color: #ffffff;
        }
        .header-top {
          font-family: 'Arial Black', 'Arial', sans-serif;
          font-weight: 900;
          font-size: 13.5px;
          margin-bottom: 2px;
          letter-spacing: -0.2px;
        }
        hr.top-line {
          border: none;
          border-top: 1px solid #000000;
          margin: 6px 0 15px 0;
        }
        .title-section {
          text-align: center;
          font-size: 20px;
          font-weight: bold;
          margin-bottom: 15px;
          letter-spacing: 0.5px;
        }
        hr.divider-line {
          border: none;
          border-top: 1px solid #cccccc;
          margin: 10px 0;
        }
        .form-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 30px;
          margin-top: 15px;
        }
        .form-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
        }
        .form-group-full {
          display: flex;
          width: 100%;
        }
        .form-label {
          color: #333333;
          font-size: 13px;
          width: 80px;
          text-align: right;
          margin-right: 20px;
        }
        .form-value {
          font-weight: bold;
          font-size: 13px;
        }
        .inline-fields {
          display: flex;
          justify-content: space-between;
          width: 100%;
          align-items: baseline;
          flex-wrap: wrap;
        }
        .inline-item {
          display: flex;
          gap: 10px;
          align-items: baseline;
        }
        .inline-label {
          color: #333333;
        }
        .section-title {
          text-align: center;
          font-size: 14px;
          font-weight: bold;
          letter-spacing: 2px;
          margin: 30px 0 15px 0;
          text-transform: uppercase;
        }
        table.members-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 25px;
        }
        table.members-table th {
          border-top: 1px solid #000000;
          border-bottom: 1px solid #000000;
          padding: 8px 0;
          font-weight: bold;
          font-size: 12px;
        }
        table.members-table td {
          font-size: 12px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          border-top: 1px solid #000000;
          padding-top: 15px;
          margin-top: 10px;
          font-size: 13px;
        }
        .summary-item {
          display: flex;
          gap: 15px;
          align-items: baseline;
        }
        .summary-label {
          color: #000000;
        }
        .summary-value {
          font-weight: bold;
          font-size: 14px;
        }
        .legal-footer {
          position: fixed;
          bottom: 20px;
          left: 0;
          right: 0;
          text-align: center;
          font-size: 10.5px;
          font-style: italic;
          color: #333333;
        }
        @media print {
          body {
            margin: 0;
          }
        }
      </style>
    </head>
    <body>
      <div class="header-top">SE.SE.VI. GESTORA</div>
      <hr class="top-line" />
      <div class="title-section">Ficha Socio</div>
      <hr class="divider-line" />
      
      <!-- Styled Fields exactly matching screenshot layout -->
      <div class="form-container">
        <!-- Row 1: Afiliado & Ficha Nro -->
        <div class="form-row">
          <div style="display: flex; gap: 20px; width: 70%; align-items: baseline;">
            <span class="form-label">Afiliado</span>
            <span class="form-value" style="font-size: 14.5px; text-transform: uppercase;">${afiliado.nombre}</span>
          </div>
          <div style="display: flex; gap: 20px; align-items: baseline;">
            <span class="inline-label">Ficha Nro.</span>
            <span class="form-value" style="font-size: 14.5px;">${afiliado.id_afiliado}</span>
          </div>
        </div>
        
        <!-- Row 2: Domicilio -->
        <div class="form-row">
          <div class="form-group-full" style="align-items: baseline;">
            <span class="form-label">Domicilio</span>
            <span class="form-value" style="text-transform: uppercase;">${afiliado.domicilio || 'No especificado'}</span>
          </div>
        </div>
        
        <!-- Row 3: Plan, Zona, Fec.Alta, Fec.Vigencia -->
        <div class="form-row">
          <div class="inline-fields">
            <div class="inline-item" style="width: 45%;">
              <span class="form-label">Plan</span>
              <span class="form-value" style="text-transform: uppercase; word-break: break-word;">${afiliado.plan}</span>
            </div>
            <div class="inline-item">
              <span class="inline-label">Zona</span>
              <span class="form-value">${afiliado.zona || '0'}</span>
            </div>
            <div class="inline-item">
              <span class="inline-label">Fec.Alta</span>
              <span class="form-value">${afiliado.fecha_afiliacion}</span>
            </div>
            <div class="inline-item">
              <span class="inline-label">Fec.Vigencia</span>
              <span class="form-value">${afiliado.fecvigencia}</span>
            </div>
          </div>
        </div>
        
        <!-- Retro Slash indicator (lower right corner of header form) -->
        <div style="text-align: right; font-weight: bold; padding-right: 15px; margin-top: -5px; color: #333333;">
          / /
        </div>
      </div>
      
      <div class="section-title">Personas Amparadas</div>
      
      <table class="members-table">
        <thead>
          <tr>
            <th style="width: 20%; text-align: center;">Documento</th>
            <th style="width: 45%; text-align: left; padding-left: 20px;">Nombre</th>
            <th style="width: 15%; text-align: center;">Fec.Nac</th>
            <th style="width: 10%; text-align: left; padding-left: 20px;">Categoria</th>
            <th style="width: 10%; text-align: center;">Vigencia</th>
          </tr>
        </thead>
        <tbody>
          ${familiaresRows}
        </tbody>
      </table>
      
      <div class="summary-row">
        <div class="summary-item">
          <span class="summary-label">Cantidad de Personas</span>
          <span class="summary-value">${activeFamiliares.length}</span>
        </div>
        <div class="summary-item" style="padding-right: 40px;">
          <span class="summary-label">Valor de la Cuota</span>
          <span class="summary-value">${formatCurrency(afiliado.total_afiliacion || 0.0)}</span>
        </div>
      </div>
      
      <div class="legal-footer">
        La datos de la presente son meramente informativos y reflejan parcialmente la informacion de la ficha respectiva
      </div>
      
      <script>
        window.onload = function() {
          window.print();
          setTimeout(function() { window.close(); }, 500);
        }
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
};

/**
 * Prints the current account detailed report
 * @param {Object} header The affiliate header info (including id_afiliado, nombre, estado, total_afiliacion)
 * @param {Array} rows The movements list
 * @param {Object} totales The calculated totals
 */
export const printCtaCte = (header, rows, totales) => {
  if (!header || !rows || !totales) return;

  const printWindow = window.open('', '_blank', 'width=900,height=800');
  if (!printWindow) {
    alert('Por favor, permite las ventanas emergentes (popups) para poder imprimir.');
    return;
  }

  const rowsHtml = rows.map(r => `
    <tr>
      <td style="text-align: center; font-family: monospace; border-bottom: 1px dotted #cccccc; padding: 6px 0;">${r.mes}</td>
      <td style="text-align: center; font-family: monospace; border-bottom: 1px dotted #cccccc;">${r.anio}</td>
      <td style="text-align: left; border-bottom: 1px dotted #cccccc;">${r.compr}</td>
      <td style="text-align: center; font-family: monospace; border-bottom: 1px dotted #cccccc;">${r.fecha || ''}</td>
      <td style="text-align: center; font-family: monospace; border-bottom: 1px dotted #cccccc;">${r.ref_pago || ''}</td>
      <td style="text-align: right; font-family: monospace; border-bottom: 1px dotted #cccccc;">${formatCurrency(r.importe)}</td>
      <td style="text-align: right; font-family: monospace; border-bottom: 1px dotted #cccccc;">${r.imppag > 0 ? formatCurrency(r.imppag) : ''}</td>
      <td style="text-align: right; font-family: monospace; border-bottom: 1px dotted #cccccc; font-weight: ${r.saldo > 0 ? 'bold' : 'normal'}; color: ${r.saldo > 0 ? '#d9534f' : 'inherit'};">
        ${r.saldo > 0 ? formatCurrency(r.saldo) : ''}
      </td>
      <td style="text-align: center; font-family: monospace; border-bottom: 1px dotted #cccccc;">${r.fecpag || '//'}</td>
      <td style="text-align: center; font-family: monospace; border-bottom: 1px dotted #cccccc;">${r.id_caja}</td>
    </tr>
  `).join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>CtaCte - ${header.nombre}</title>
      <style>
        @page {
          size: A4 portrait;
          margin: 15mm 15mm 15mm 15mm;
        }
        body {
          font-family: 'Arial', sans-serif;
          margin: 0;
          padding: 0;
          color: #000000;
          font-size: 12px;
          line-height: 1.3;
        }
        .title-section {
          text-align: center;
          margin-bottom: 20px;
        }
        .title {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 3px;
        }
        .subtitle {
          font-size: 13px;
          font-weight: bold;
          color: #333333;
        }
        .header-box {
          border: 1px solid #000000;
          padding: 10px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          font-size: 12px;
        }
        .header-label {
          font-weight: bold;
        }
        table.ctacte-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        table.ctacte-table th {
          border-top: 1px solid #000000;
          border-bottom: 1px solid #000000;
          padding: 6px 4px;
          font-weight: bold;
          font-size: 11px;
          background-color: #f5f5f5;
        }
        table.ctacte-table td {
          font-size: 11px;
        }
        .totales-row {
          border-top: 1px solid #000000;
          border-bottom: 2px double #000000;
          font-weight: bold;
          background-color: #fafafa;
        }
        .totales-row td {
          padding: 8px 4px;
          font-size: 12px;
        }
      </style>
    </head>
    <body>
      <div class="title-section">
        <div class="title">Consulta de Cuenta Corriente</div>
        <div class="subtitle">Por Afiliado</div>
      </div>
      
      <div class="header-box">
        <div>
          <span class="header-label">Afiliado:</span> ${header.id_afiliado} &nbsp;&nbsp;&nbsp; <strong>${header.nombre}</strong>
        </div>
        <div>
          <span class="header-label">Estado:</span> ${header.estado}
        </div>
        <div>
          <span class="header-label">Total Afiliación:</span> ${formatCurrency(header.total_afiliacion)}
        </div>
      </div>
      
      <table class="ctacte-table">
        <thead>
          <tr>
            <th colspan="2" style="text-align: center; border-right: 1px solid #dddddd; width: 10%;">Periodo</th>
            <th style="text-align: left; width: 10%;">Compr.</th>
            <th style="text-align: center; width: 12%;">Fecha</th>
            <th style="text-align: center; width: 12%;">Ref.Pago</th>
            <th style="text-align: right; width: 12%;">A Cobrar</th>
            <th style="text-align: right; width: 12%;">Cobrado</th>
            <th style="text-align: right; width: 12%;">Saldo</th>
            <th style="text-align: center; width: 12%;">Fec.Pago</th>
            <th style="text-align: center; width: 8%;">Caja</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
          <tr class="totales-row">
            <td colspan="5" style="text-align: right; padding-right: 15px;">Totales:</td>
            <td style="text-align: right; font-family: monospace;">${formatCurrency(totales.a_cobrar)}</td>
            <td style="text-align: right; font-family: monospace;">${formatCurrency(totales.cobrado)}</td>
            <td style="text-align: right; font-family: monospace;">${formatCurrency(totales.saldo)}</td>
            <td colspan="2"></td>
          </tr>
        </tbody>
      </table>
      
      <script>
        window.onload = function() {
          window.print();
          setTimeout(function() { window.close(); }, 500);
        }
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
};
